'use client';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { useFreeShippingMessagingExperiment } from '../useFreeShippingMessagingExperiment';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { ShippingId } from '@/modules/shipping/types';
import { useCountryHeader } from '@/modules/user/useCountryHeader';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const ILLUSTRATION_SRC = `${process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST}/web/assets/illustrations/post-purchase.webp`;

export function PDPBanner() {
  const t = useTranslations('common');
  const countryCode = useCountryHeader();
  const [authenticated, currentUser] = useCurrentUser();
  const { data: product } = useProductBySlugBasicPDP();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  const shippingId = product?.shipping_method?.shipping_id;
  const isUSDepopShipping = shippingId === ShippingId.USPS;
  const isEligible =
    !authenticated && countryCode?.toUpperCase() === 'US' && isUSDepopShipping;
  const inVariant = useFreeShippingMessagingExperiment(isEligible);

  if (!inVariant) {
    return null;
  }

  function handleSignupClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: 'free_shipping_pdp_banner_signup',
      schemaVersion: '2.0',
    });
  }

  return (
    <section
      className={styles.banner}
      aria-label={t('USFreeShippingBanner.AriaLabel')}
    >
      <img
        className={styles.illustration}
        src={ILLUSTRATION_SRC}
        alt={t('USFreeShippingBanner.IllustrationAlt')}
      />
      <div className={styles.content}>
        <div className={styles.text}>
          <Caption
            as="span"
            variant="lg-bold"
            colour="base"
            className={styles.title}
          >
            {t('USFreeShippingBanner.Title')}
          </Caption>
          <Caption
            as="span"
            variant="sm-regular"
            colour="base"
            className={styles.body}
          >
            {t('USFreeShippingBanner.PdpBody')}
          </Caption>
        </div>
        <ExtendedLink
          href="/signup"
          className={styles.signupLink}
          onClick={handleSignupClick}
        >
          <Caption as="span" variant="sm-bold" colour="base">
            {t('USFreeShippingBanner.SignupCta')}
          </Caption>
        </ExtendedLink>
      </div>
    </section>
  );
}
